import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  deleteInvoiceDocument,
  deleteInvoiceLink,
  getInvoiceDownload,
  saveInvoiceExtraction,
} from '@/api/invoices';
import type {
  InvoiceExtractionIn,
  InvoiceExtractionOut,
  InvoiceLinkOut,
} from '@/api/invoices';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DOCUMENT_TYPES,
  InvoiceFactsFields,
  labelForOption,
  SUPPLY_DESCRIPTION_QUALITIES,
  TAX_INDICATIONS,
} from '@/components/InvoiceFactsFields';
import {
  clearInvoiceDraft,
  draftHasChanges,
  invoiceDraftKey,
  loadInvoiceDraft,
  saveInvoiceDraft,
} from '@/lib/invoiceDraft';
import { formatInvoiceEffectiveRate } from '@/lib/invoiceEffectiveRate';
import { previewKindForFilename, resolvePreviewUrl } from '@/lib/invoicePreview';
import { type InvoiceFactsPrefill, seedDraftWithPrefill } from '@/lib/invoicePrefill';

/** Inline preview of the invoice(s) attached to a transaction — images
 *  render in an <img>, PDFs in an <iframe>, anything else falls back to a
 *  download link. The document renders portrait/vertical in a
 *  width-constrained column so tall receipts stay legible. Reusable across
 *  the finding detail page (first consumer), the flashcard, and the portal.
 *  The signed URL comes from `getInvoiceDownload`; the captured facts come
 *  from the link + any stored extraction and are editable in place. */
export function InvoicePreview({
  customerSlug,
  links,
  extractionsByDocument,
  downloadUrlsByDocument,
  prefill,
  supplierNameOptions,
}: {
  customerSlug: string | null;
  links: InvoiceLinkOut[] | undefined;
  extractionsByDocument: Map<string, InvoiceExtractionOut>;
  /** Presigned URLs bundled into the transaction-documents response
   *  (document id -> URL). When the shown document has one, the preview
   *  loads without the separate `invoice-download` round-trip. Omitted by
   *  callers that don't request bundled URLs (portal, flashcard). */
  downloadUrlsByDocument?: Map<string, string>;
  /** GL-derived facts (from the finding's evidence) used to pre-fill the
   *  invoice editor's blank fields when a practitioner starts capturing.
   *  Omitted by callers with no transaction context (portal). */
  prefill?: InvoiceFactsPrefill;
  /** Known supplier names for the searchable supplier field. */
  supplierNameOptions?: string[];
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(0);
  const [confirming, setConfirming] = useState<'unattach' | 'delete' | null>(null);

  const docs = links ?? [];
  const idx = Math.min(selected, Math.max(0, docs.length - 1));
  const link = docs[idx] ?? null;
  const documentId = link?.source_document_id ?? '';

  // Only take the bundled-URL shortcut when the link also carries a filename
  // — the preview kind is inferred from it. Without a filename we fall back
  // to the download query, which returns the filename too, so a valid
  // PDF/image isn't misclassified as `other` and downgraded to a link.
  const bundledUrl =
    documentId && link?.filename ? (downloadUrlsByDocument?.get(documentId) ?? null) : null;

  // Only fetch the presigned URL separately when it wasn't bundled into the
  // transaction-documents response — that's the round-trip the finding
  // detail page skips.
  const download = useQuery({
    queryKey: ['invoice-download', customerSlug, documentId, userKey],
    enabled: isSignedIn && customerSlug !== null && documentId !== '' && bundledUrl === null,
    queryFn: async () =>
      getInvoiceDownload(customerSlug!, documentId, (await getIdToken()) ?? ''),
    staleTime: 30 * 60 * 1000,
  });

  const previewUrl = resolvePreviewUrl(bundledUrl, download.data?.url);

  // Skeleton stays up until the image/PDF bytes actually paint (onLoad),
  // not just until the URL resolves — the S3 byte-fetch is where most of
  // the perceived lag is. Reset when the shown document changes.
  const [contentLoaded, setContentLoaded] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  useEffect(() => {
    setContentLoaded(false);
    setMediaError(false);
  }, [documentId]);
  // Safety net: some browsers don't fire `load` for a cross-origin PDF in
  // an iframe, so force the skeleton off after a few seconds rather than
  // let it linger over already-rendered content.
  useEffect(() => {
    if (previewUrl === null || contentLoaded) return;
    const timer = setTimeout(() => setContentLoaded(true), 6000);
    return () => clearTimeout(timer);
  }, [previewUrl, contentLoaded]);

  // Unattach: detach the shown link from the transaction; the document
  // stays in the pool for re-assignment. Reset the selector so the clamp
  // lands on a still-present doc after the list shrinks.
  const unattach = useMutation({
    mutationFn: async (linkId: string) =>
      deleteInvoiceLink(customerSlug!, linkId, (await getIdToken()) ?? ''),
    onSuccess: () => {
      setConfirming(null);
      setSelected(0);
      void qc.invalidateQueries({ queryKey: ['transaction-documents', customerSlug] });
    },
  });

  // Delete: permanently remove the document (+ S3 bytes + every link).
  const destroy = useMutation({
    mutationFn: async (docId: string) =>
      deleteInvoiceDocument(customerSlug!, docId, (await getIdToken()) ?? ''),
    onSuccess: () => {
      setConfirming(null);
      setSelected(0);
      void qc.invalidateQueries({ queryKey: ['transaction-documents', customerSlug] });
    },
  });

  const actionPending = unattach.isPending || destroy.isPending;

  // Clear any prior error so a second confirm doesn't render a stale
  // "couldn't <other action>" message.
  const openConfirm = (which: 'unattach' | 'delete') => {
    unattach.reset();
    destroy.reset();
    setConfirming(which);
  };

  if (docs.length === 0 || link === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Invoice</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-brand-slate">
            No invoice attached to this transaction yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  const filename = link.filename ?? download.data?.filename ?? null;
  const kind = previewKindForFilename(filename);
  const extraction = extractionsByDocument.get(link.source_document_id) ?? null;

  return (
    <Card>
      <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
        <DialogHeader>
          <DialogTitle>
            {confirming === 'delete' ? 'Delete this invoice?' : 'Unattach this invoice?'}
          </DialogTitle>
          <DialogDescription>
            {confirming === 'delete'
              ? 'This permanently deletes the invoice document and its file. It is removed from every transaction it is attached to and cannot be recovered.'
              : 'This removes the invoice from this transaction. The document stays in your invoice pool, where you can re-assign it later in the Invoice tab’s flashcard view — nothing is deleted.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={actionPending}
            onClick={() => setConfirming(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={actionPending}
            onClick={() => {
              if (confirming === 'delete') destroy.mutate(link.source_document_id);
              else unattach.mutate(link.id);
            }}
          >
            {confirming === 'delete'
              ? destroy.isPending
                ? 'Deleting…'
                : 'Delete'
              : unattach.isPending
                ? 'Unattaching…'
                : 'Unattach'}
          </Button>
        </DialogFooter>
      </Dialog>
      <CardHeader className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">
            Invoice
            {docs.length > 1 && (
              <span className="ml-2 text-xs font-normal text-brand-slate">
                {docs.length} attached
              </span>
            )}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-royal underline-offset-4 hover:underline"
              >
                Open in new tab ↗
              </a>
            )}
            {customerSlug !== null && (
              <>
                <button
                  type="button"
                  onClick={() => openConfirm('unattach')}
                  className="text-xs text-brand-royal underline-offset-4 hover:underline"
                  title="Detach this invoice from the transaction; it returns to the invoice pool"
                >
                  Unattach
                </button>
                <button
                  type="button"
                  onClick={() => openConfirm('delete')}
                  className="text-xs text-destructive underline-offset-4 hover:underline"
                  title="Permanently delete this invoice document"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
        {(unattach.isError || destroy.isError) && (
          <p className="text-xs text-destructive">
            Couldn&rsquo;t {unattach.isError ? 'unattach' : 'delete'} the invoice — try again.
          </p>
        )}

        {docs.length > 1 && (
          <div className="flex flex-wrap gap-1">
            {docs.map((d, i) => (
              <button
                key={d.id}
                type="button"
                disabled={actionPending}
                onClick={() => {
                  unattach.reset();
                  destroy.reset();
                  setSelected(i);
                  setConfirming(null);
                }}
                className={
                  i === idx
                    ? 'rounded-md border border-brand-royal bg-brand-royal/10 px-2 py-1 text-xs text-brand-navy'
                    : 'rounded-md border border-border px-2 py-1 text-xs text-brand-slate transition-colors hover:border-brand-royal/40'
                }
              >
                {d.filename ?? `Document ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        <EditableInvoiceFacts
          key={documentId}
          customerSlug={customerSlug}
          documentId={documentId}
          link={link}
          extraction={extraction}
          prefill={prefill}
          supplierNameOptions={supplierNameOptions}
        />
      </CardHeader>

      <CardContent>
        {previewUrl !== null ? (
          mediaError ? (
            // The bytes failed to load — a broken <img> with no explanation
            // is worse than an actionable message + a way out.
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
              <p className="font-medium text-destructive">Couldn&rsquo;t display the invoice.</p>
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-brand-royal underline-offset-4 hover:underline"
              >
                Open {filename ?? 'the document'} in a new tab ↗
              </a>
            </div>
          ) : (
            // Portrait column: constrain the width so a tall receipt renders
            // vertically at a legible size instead of being squashed to fit
            // the full page width. Media + skeleton share one grid cell so the
            // skeleton (later in DOM = on top) covers the media until its bytes
            // paint (onLoad) — feedback during the S3 fetch — and its
            // min-height reserves space so a not-yet-sized <img> doesn't
            // collapse the layout.
            <div className="mx-auto grid w-full max-w-3xl">
              <div className="col-start-1 row-start-1">
                {kind === 'image' ? (
                  <img
                    src={previewUrl}
                    alt={filename ?? 'Attached invoice'}
                    className="w-full rounded-md border border-border"
                    onLoad={() => setContentLoaded(true)}
                    onError={() => setMediaError(true)}
                  />
                ) : kind === 'pdf' ? (
                  <iframe
                    src={previewUrl}
                    title={filename ?? 'Attached invoice'}
                    className="h-[85vh] w-full rounded-md border border-border"
                    onLoad={() => setContentLoaded(true)}
                  />
                ) : (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-brand-royal underline-offset-4 hover:underline"
                  >
                    Download {filename ?? 'attached document'} ↗
                  </a>
                )}
              </div>
              {kind !== 'other' && !contentLoaded && (
                <div className="col-start-1 row-start-1">
                  <InvoiceSkeleton />
                </div>
              )}
            </div>
          )
        ) : download.isError ? (
          <p className="text-sm text-destructive">
            Couldn't load the invoice document — try refreshing.
          </p>
        ) : (
          // URL not resolved yet — skeleton while the presigned URL loads.
          <InvoiceSkeleton />
        )}
      </CardContent>
    </Card>
  );
}

/** Animated placeholder shown while the invoice URL resolves and its bytes
 *  paint. The min-height reserves a receipt-shaped column so the layout
 *  doesn't jump when the real document arrives. */
function InvoiceSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading invoice"
      className="flex h-full min-h-[60vh] w-full animate-pulse flex-col gap-3 rounded-md border border-border bg-card p-4"
    >
      <div className="h-6 w-1/3 rounded bg-muted" />
      <div className="h-4 w-1/2 rounded bg-muted" />
      <div className="mt-2 h-px w-full bg-border" />
      <div className="h-4 w-full rounded bg-muted" />
      <div className="h-4 w-5/6 rounded bg-muted" />
      <div className="h-4 w-2/3 rounded bg-muted" />
      <div className="mt-auto h-4 w-1/4 self-end rounded bg-muted" />
      <span className="sr-only">Loading invoice…</span>
    </div>
  );
}

function asText(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

/** The captured invoice facts, editable in place. Reads extraction-first
 *  (the practitioner's labelled reading of the document) falling back to
 *  the link facts, and writes edits back to the extraction label. Shares
 *  the full field set with the invoice workbench via `InvoiceFactsFields`.
 *
 *  NOTE: `saveInvoiceExtraction` overwrites the stored label in place. We do
 *  NOT yet keep an audit trail of invoice edits (who changed which field,
 *  when) — that history is a future need once practitioners rely on these
 *  values for the filing. Deliberately not built here.
 */
function EditableInvoiceFacts({
  customerSlug,
  documentId,
  link,
  extraction,
  prefill,
  supplierNameOptions,
}: {
  customerSlug: string | null;
  documentId: string;
  link: InvoiceLinkOut;
  extraction: InvoiceExtractionOut | null;
  prefill?: InvoiceFactsPrefill;
  supplierNameOptions?: string[];
}) {
  const { getIdToken, userKey } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  // Extraction-first, link fallback for the facts the link carries; the
  // richer SOR/91-45 fields (tax indication, recipient, terms, supply
  // description, notes) live only on the extraction label.
  // The Out model types the enum fields as free strings; narrow them back to
  // the In enums on the way into the editor (values originate from the same
  // backend enum, so the cast is sound).
  const current: InvoiceExtractionIn = {
    document_type:
      (extraction?.document_type ?? null) as InvoiceExtractionIn['document_type'],
    supplier_name_on_invoice:
      extraction?.supplier_name_on_invoice ?? link.supplier_name_on_invoice ?? null,
    supplier_bn: extraction?.supplier_bn ?? link.supplier_bn ?? null,
    invoice_number: extraction?.invoice_number ?? link.invoice_number ?? null,
    invoice_date: extraction?.invoice_date ?? link.invoice_date ?? null,
    invoice_total: extraction?.invoice_total ?? link.invoice_total ?? null,
    tax_indication:
      (extraction?.tax_indication ?? null) as InvoiceExtractionIn['tax_indication'],
    tax_shown: extraction?.tax_shown ?? null,
    recipient_name: extraction?.recipient_name ?? null,
    payment_terms: extraction?.payment_terms ?? null,
    supply_description_quality:
      (extraction?.supply_description_quality ??
        null) as InvoiceExtractionIn['supply_description_quality'],
    notes: extraction?.notes ?? null,
  };

  // The draft to open a fresh edit with: `current` plus GL prefill filling
  // any blank prefillable field (OCR / saved facts always win). Only used
  // to seed a new edit — the read-only view below always shows `current`,
  // so a GL prefill never masquerades as a captured invoice fact.
  const seeded = prefill ? seedDraftWithPrefill(current, prefill) : current;

  const [draft, setDraft] = useState<InvoiceExtractionIn>(current);
  // A persisted draft from a prior edit that ended without saving (page
  // unmounted, tab closed) — offered for restore when we're not editing.
  const [restorable, setRestorable] = useState<InvoiceExtractionIn | null>(null);
  const draftKey = invoiceDraftKey(userKey, customerSlug, documentId);

  // Reseed from the source record whenever it changes and we're not mid-edit.
  useEffect(() => {
    if (!editing) setDraft(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraction, link, editing]);

  // Surface a restorable draft when not editing (INP-502 safety net).
  useEffect(() => {
    if (editing) return;
    const saved = loadInvoiceDraft(draftKey);
    setRestorable(draftHasChanges(saved, current) ? saved : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, extraction, link, editing]);

  // Persist every keystroke while editing so an unexpected unmount can't lose
  // the typed facts. Cleared on save / cancel / discard.
  useEffect(() => {
    if (editing) saveInvoiceDraft(draftKey, draft);
  }, [editing, draft, draftKey]);

  const save = useMutation({
    mutationFn: async () =>
      saveInvoiceExtraction(customerSlug!, documentId, draft, (await getIdToken()) ?? ''),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-extraction', customerSlug, documentId] });
      qc.invalidateQueries({ queryKey: ['transaction-documents', customerSlug] });
      clearInvoiceDraft(draftKey);
      setRestorable(null);
      setEditing(false);
    },
  });

  if (!editing) {
    const facts: [string, string][] = [];
    if (current.document_type)
      facts.push(['Type', labelForOption(DOCUMENT_TYPES, current.document_type)]);
    if (current.supplier_name_on_invoice)
      facts.push(['Supplier', current.supplier_name_on_invoice]);
    if (current.supplier_bn) facts.push(['BN', current.supplier_bn]);
    if (current.invoice_number) facts.push(['Invoice #', current.invoice_number]);
    if (current.invoice_date) facts.push(['Date', current.invoice_date]);
    if (asText(current.invoice_total)) facts.push(['Total', asText(current.invoice_total)]);
    if (current.tax_indication)
      facts.push(['Tax', labelForOption(TAX_INDICATIONS, current.tax_indication)]);
    if (asText(current.tax_shown)) facts.push(['Tax shown', asText(current.tax_shown)]);
    const effectiveRate = formatInvoiceEffectiveRate(current.tax_shown, current.invoice_total);
    if (effectiveRate) facts.push(['Effective rate', effectiveRate]);
    if (current.recipient_name) facts.push(['Recipient', current.recipient_name]);
    if (current.payment_terms) facts.push(['Terms', current.payment_terms]);
    if (current.supply_description_quality)
      facts.push([
        'Supply desc.',
        labelForOption(SUPPLY_DESCRIPTION_QUALITIES, current.supply_description_quality),
      ]);
    if (current.notes) facts.push(['Notes', current.notes]);

    return (
      <div className="grid gap-2">
        {restorable !== null && customerSlug !== null && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
            <span>You have an unsaved draft for this invoice from an earlier edit.</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-6"
              onClick={() => {
                setDraft(restorable);
                setRestorable(null);
                setEditing(true);
              }}
            >
              Resume editing
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6"
              onClick={() => {
                clearInvoiceDraft(draftKey);
                setRestorable(null);
              }}
            >
              Discard
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between gap-2">
          {facts.length > 0 ? (
            <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {facts.map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-1">
                  <dt className="uppercase tracking-wide text-brand-slate">{k}</dt>
                  <dd className="font-medium break-words text-brand-navy">{v}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-xs text-brand-slate">No captured invoice facts.</p>
          )}
          {/* Hidden while a restorable draft exists — starting a fresh edit
              would persist `current` over the stored draft and destroy it.
              Force an explicit Resume/Discard first. */}
          {customerSlug !== null && restorable === null && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => {
                setDraft(seeded);
                setEditing(true);
              }}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      className="grid gap-3 rounded-md border border-border bg-muted/20 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save.mutate();
      }}
      onKeyDown={(e) => {
        // Cmd/Ctrl-S saves without leaving the keyboard — practitioners work
        // long invoice queues and asked to bank progress fast (INP-502).
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
          e.preventDefault();
          if (!save.isPending) void save.mutate();
        }
      }}
    >
      <InvoiceFactsFields
        value={draft}
        onChange={setDraft}
        supplierNameOptions={supplierNameOptions}
      />
      {save.isError && (
        <p className="text-xs text-destructive">
          Couldn't save — check your connection and try again.
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={save.isPending}
          onClick={() => {
            clearInvoiceDraft(draftKey);
            setRestorable(null);
            setDraft(current);
            setEditing(false);
          }}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
